import { useNavigate } from '@tanstack/react-router'
import { useCallback, useEffect } from 'react'

import {
  isSingleProjectPlan,
  useAccount,
  useIsTrialAccount,
  useReconciliationEntitled,
} from '@/modules/account'
import { useUser } from '@/modules/auth'
import type { Project } from '@/modules/projects'
import { CreateProjectDialog, useProjects } from '@/modules/projects'
import { useDialogActions } from '@/stores/dialog'
import {
  loadDismissedAt,
  loadDoneSteps,
  loadWelcomedAt,
  persistDismissedAt,
  persistWelcomedAt,
} from './prefs'
import { joinedAfterLaunch } from './progress'
import { gettingStartedSteps, nextOpenStep } from './steps'
import { useGettingStartedStore } from './store'

export const useIsSingleProjectAccount = () =>
  isSingleProjectPlan(useAccount()?.planKind)

export const useGettingStartedSteps = () =>
  gettingStartedSteps(useReconciliationEntitled(), useIsSingleProjectAccount())

// Reads the signed-in user's saved choices once per user. Until then both count
// as made, so a first render never flashes the nudges or the welcome.
export const useGettingStartedChoices = () => {
  const userId = useUser()?.id ?? null
  const hydratedForUser = useGettingStartedStore(
    (state) => state.hydratedForUser,
  )
  const hydrate = useGettingStartedStore((state) => state.hydrate)

  const storageUser = userId ?? 'anon'
  useEffect(() => {
    if (hydratedForUser === storageUser) return
    hydrate(storageUser, {
      dismissedAt: loadDismissedAt(userId),
      welcomedAt: loadWelcomedAt(userId),
      doneSteps: loadDoneSteps(userId),
    })
  }, [userId, storageUser, hydratedForUser, hydrate])

  return { userId, hydrated: hydratedForUser !== null }
}

export const useGettingStartedDismissed = (): {
  dismissed: boolean
  dismiss: () => void
} => {
  const { userId, hydrated } = useGettingStartedChoices()
  const dismissedAt = useGettingStartedStore((state) => state.dismissedAt)
  const markDismissed = useGettingStartedStore((state) => state.dismiss)

  const dismiss = useCallback(() => {
    const at = new Date().toISOString()
    persistDismissedAt(userId, at)
    markDismissed(at)
  }, [userId, markDismissed])

  return { dismissed: !hydrated || dismissedAt !== null, dismiss }
}

export const useGettingStartedWelcomed = (): {
  welcomed: boolean
  markWelcomed: () => void
} => {
  const { userId, hydrated } = useGettingStartedChoices()
  const welcomedAt = useGettingStartedStore((state) => state.welcomedAt)
  const welcome = useGettingStartedStore((state) => state.welcome)

  const markWelcomed = useCallback(() => {
    const at = new Date().toISOString()
    persistWelcomedAt(userId, at)
    welcome(at)
  }, [userId, welcome])

  return { welcomed: !hydrated || welcomedAt !== null, markWelcomed }
}

// The nudges show to people who joined after the tour shipped, until they
// dismiss it or finish every step. A trial has its own first run instead.
export const useShowGettingStartedNudges = () => {
  const isTrial = useIsTrialAccount()
  const { dismissed } = useGettingStartedDismissed()
  const userCreatedAt = useUser()?.created_at ?? null
  const steps = useGettingStartedSteps()
  const doneSteps = useGettingStartedStore((state) => state.doneSteps)
  return (
    !isTrial &&
    !dismissed &&
    joinedAfterLaunch(userCreatedAt) &&
    nextOpenStep(steps, doneSteps) !== null
  )
}

// The welcome asks once, on first entry, the people the nudges are for.
export const useShowGettingStartedWelcome = () => {
  const { welcomed } = useGettingStartedWelcomed()
  const showNudges = useShowGettingStartedNudges()
  return !welcomed && showNudges
}

// The project the step buttons open: the newest one with processed statements,
// or the newest one at all while nothing has finished yet.
export const useGettingStartedProject = (): Project | null => {
  const projects = useProjects()
  const newestFirst = [...projects].sort((left, right) =>
    right.createdAt.localeCompare(left.createdAt),
  )
  return (
    newestFirst.find((project) => project.hasTransactions) ??
    newestFirst[0] ??
    null
  )
}

export const useOpenCreateProject = () => {
  const navigate = useNavigate()
  const { openDialog } = useDialogActions()
  return useCallback(() => {
    navigate({ to: '/projects' }).then(() => {
      openDialog(CreateProjectDialog.id)
    })
  }, [navigate, openDialog])
}
